import * as React from 'react';
import { ApiHelper } from '../ApiHelper';
import { useParams, useSearchParams } from "react-router-dom";
import { DetailsList, IDetailsRowProps, DetailsRow } from '@fluentui/react/lib/DetailsList';
import { Dropdown, IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { Icon } from '@fluentui/react/lib/Icon';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { useConfig } from '../configContext';
import { Process } from '../interfaces/Service';
import ProfilerIcon from '../Profiler.ico';

function RenderEnvIcons(profiles, friendlyName) {
    const profile = profiles.find(p => p.friendlyName === friendlyName);

    if (!profile) {
        return <span style={{ color: '#888' }}>No data</span>;
    }

    return (
        <div style={{ display: 'inline-flex', gap: '24px' }}>
            <a
                href={`https://azprofilerclko.azurewebsites.net/ClickOnce/AzureProfilerViewer.application?Blob=${profile.base64SasUrl}&NewPipeline=true`}
                target="_blank"
                rel="noopener noreferrer"
                title={`Open ${profile.name} in Azure Profiler Viewer. May require VPN.`}
                style={{ display: 'inline-flex', alignItems: 'center' }}
            >
                <img src={ProfilerIcon} alt="Open in viewer" style={{ width: '14px', height: '14px' }} />
            </a>
            <a
                href={profile.sasUrl}
                target="_blank"
                rel="noopener noreferrer"
                title={`Download ${profile.name} profile`}
                style={{ display: 'inline-flex', alignItems: 'center' }}
            >
                <Icon iconName="Download" style={{ fontSize: '14px' }} />
            </a>
        </div>
    );
}

export function ProfileList() {
    const { serviceName } = useParams();
    const [searchParams] = useSearchParams();
    const config = useConfig();

    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [profiles, setProfiles] = React.useState<any[]>([]);

    const [selectedEnvId, setSelectedEnvId] = React.useState<number | undefined>(undefined);
    const [selectedProcessId, setSelectedProcessId] = React.useState<number | undefined>(undefined);
    const [selectedMetricId, setSelectedMetricId] = React.useState<number | undefined>(undefined);

    const service = React.useMemo(() => {
        return config?.services?.find(s => s.name === serviceName);
    }, [config, serviceName]);

    // Initialize selections from defaultConfig or first available
    React.useEffect(() => {
        if (!service || selectedEnvId !== undefined) return;

        const defaultEnvId = service.defaultConfig?.environmentId ?? service.Environments?.[0]?.id;
        const defaultProcessId = service.defaultConfig?.processId ?? service.Processes?.[0]?.id;
        const defaultProcess: Process | undefined = service.Processes?.find(p => p.id === defaultProcessId);
        const defaultMetricId = service.defaultConfig?.metricId
            ?? Object.values(defaultProcess?.metrics ?? {})[0]?.id;

        setSelectedEnvId(defaultEnvId);
        setSelectedProcessId(defaultProcessId);
        setSelectedMetricId(defaultMetricId);
    }, [service]);

    // Fetch profiles when selections are ready
    React.useEffect(() => {
        if (!serviceName || selectedEnvId === undefined || selectedProcessId === undefined || selectedMetricId === undefined) return;

        async function fetchProfiles() {
            setLoading(true);
            setError(null);

            try {
                const flush = searchParams.get("flush");
                let apiUrl = `/api/v3/service/${serviceName}/aggregatedprofiles/${selectedEnvId}/${selectedProcessId}/${selectedMetricId}`;
                if (flush !== null) {
                    apiUrl += `?flush=${flush}`;
                }

                const data = await ApiHelper<any[]>(apiUrl);
                setProfiles(data || []);
            } catch (err: any) {
                setError(err.message || 'Failed to load profiles');
                setProfiles([]);
            } finally {
                setLoading(false);
            }
        }

        fetchProfiles();
    }, [serviceName, selectedEnvId, selectedProcessId, selectedMetricId, searchParams]);

    // Dropdown options — all config-driven
    const envOptions: IDropdownOption[] = React.useMemo(() => {
        return service?.Environments?.map(env => ({ key: env.id, text: env.name })) ?? [];
    }, [service]);

    const processOptions: IDropdownOption[] = React.useMemo(() => {
        return service?.Processes?.map(p => ({ key: p.id, text: p.friendlyName })) ?? [];
    }, [service]);

    const selectedProcess: Process | undefined = React.useMemo(() => {
        return service?.Processes?.find(p => p.id === selectedProcessId);
    }, [service, selectedProcessId]);

    const metricOptions: IDropdownOption[] = React.useMemo(() => {
        const metricDisplayNames: Record<string, string> = { OnCPU: 'CPU', OffCPU: 'Waits' };
        return Object.values(selectedProcess?.metrics ?? {}).map(m => ({
            key: m.id,
            text: metricDisplayNames[m.name] ?? m.name,
        }));
    }, [selectedProcess]);

    const selectedEnvName = React.useMemo(() => {
        return service?.Environments?.find(e => e.id === selectedEnvId)?.name;
    }, [service, selectedEnvId]);

    function handleEnvChange(newEnvId: number) {
        setSelectedEnvId(newEnvId);
        const defaultProcessId = service?.Processes?.[0]?.id;
        const defaultProcess: Process | undefined = service?.Processes?.[0];
        const firstMetricId = Object.values(defaultProcess?.metrics ?? {})[0]?.id;
        setSelectedProcessId(defaultProcessId);
        setSelectedMetricId(firstMetricId);
    }

    function handleProcessChange(newProcessId: number) {
        setSelectedProcessId(newProcessId);
        const proc: Process | undefined = service?.Processes?.find(p => p.id === newProcessId);
        const firstMetricId = Object.values(proc?.metrics ?? {})[0]?.id;
        setSelectedMetricId(firstMetricId);
    }

    const columns = [
        {
            key: 'date',
            name: 'Date',
            fieldName: 'dateAsString',
            minWidth: 100,
            maxWidth: 200,
            isResizable: true,
        },
        {
            key: 'profile',
            name: selectedProcess?.friendlyName ?? 'Profile',
            minWidth: 150,
            maxWidth: 300,
            isResizable: true,
            onRender: (item) => {
                const envForDay = item.envs?.find(e => e.name === selectedEnvName);
                const profilesForCell = envForDay?.profiles ?? [];
                return RenderEnvIcons(profilesForCell, selectedProcess?.friendlyName);
            }
        }
    ];

    window.document.title = 'PR Guardian - Untrimmed profiles';

    function _onRenderRow(props: IDetailsRowProps | undefined): JSX.Element {
        if (!props) return null;
        return (
            <DetailsRow
                {...props}
                className={props.itemIndex % 2 === 0 ? 'evenRow' : 'oddRow'}
            />
        );
    }

    return (
        <div style={{ margin: '20px' }}>
            <div>&nbsp;</div>
            <div>Below are untrimmed profiles aggregated from the most utilized machines. These profiles show a lot more details than the ones listed in the viewer.</div>
            <div>&nbsp;</div>

            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                <Dropdown
                    label="Environment"
                    options={envOptions}
                    selectedKey={selectedEnvId}
                    onChange={(_, option) => handleEnvChange(option?.key as number)}
                    style={{ width: 200 }}
                />
                <Dropdown
                    label="Process"
                    options={processOptions}
                    selectedKey={selectedProcessId}
                    onChange={(_, option) => handleProcessChange(option?.key as number)}
                    style={{ width: 200 }}
                />
                <Dropdown
                    label="Metric"
                    options={metricOptions}
                    selectedKey={selectedMetricId}
                    onChange={(_, option) => setSelectedMetricId(option?.key as number)}
                    style={{ width: 200 }}
                />
            </div>

            {error && (
                <MessageBar messageBarType={MessageBarType.error}>
                    {error}
                </MessageBar>
            )}

            {loading ? (
                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                    <Spinner size={SpinnerSize.large} label="Loading profiles..." />
                </div>
            ) : profiles.length === 0 ? (
                <MessageBar messageBarType={MessageBarType.info}>
                    No profiles available for this Environment / Process / Metric in the last 60 days.
                </MessageBar>
            ) : (
                <DetailsList
                    compact={true}
                    items={profiles}
                    columns={columns}
                    onRenderRow={_onRenderRow}
                    setKey="set"
                    selectionMode={0}
                />
            )}
        </div>
    );
}
